"use client";

import type { Dispatch } from "@reduxjs/toolkit";
import { GAEvent, GAClickEvent, sendConsentGatedAnalyticsEvent } from "@/scripts/GoogleAnalytics";
import { resetReviewStateTrustPilot } from "@/redux/slice/ReviewTrustPilotSlice";
import type { TrustPilotBusinessType } from "@/api/reviewTrustPilotApi";
import type { CouponType } from "@/types/trustpilotPricing";
import { FUNNEL_TRUST_PILOT } from "@/types/funnelTypes";
import { safeClarityEvent } from "@/utils/clarity";
import { trackingService } from "@/services/TrackingService";
import { clearPurchaseIdempotency } from "@/utils/analytics/purchaseIdempotency";
import { deletePersistentCart } from "@/utils/hooks/usePersistentCart";
import { initializeUuidWithLock } from "@/utils/hooks/usePersistentCartInit";
import { sendMetaContactFormEvent, sendMetaFormInteraction, sendMetaFormSubmission, sendMetaOrderSubmitted, sendMetaReviewSelection } from "@/utils/analytics/metaPixelHelpers";
import { trackCheckoutFunnelEvent, type CheckoutErrorCategory } from "@/utils/analytics/checkoutFunnel";
import { createPlatformOrderResultSummary, persistPlatformOrderResultSummary } from "@/utils/platformOrderResultSummary";
import { logSafeError } from "@/utils/logSafeError";
import { persistPaymentSetupToken, readPaymentSetupToken, readPaymentSetupTrackingUrl, markPaymentSetupCompletionTracked } from "@/utils/paymentSetup";
import { reportToFlare } from "@/utils/reportToFlare";
import type { PaymentMethodStatus } from "@/api/orderPaymentApi";

type SuccessfulOrderResult = unknown;

export type HandleSuccessfulOrderOptions = {
  funnelType: typeof FUNNEL_TRUST_PILOT;
  checkoutTotalPrice?: string;
};

export function GA4formSubmission(platform: typeof FUNNEL_TRUST_PILOT = FUNNEL_TRUST_PILOT) {
  trackCheckoutFunnelEvent("contact_details_completed", { platform });
  safeClarityEvent("Submitted Form");
  sendMetaFormSubmission();
}

export function GA4formInteraction(platform: typeof FUNNEL_TRUST_PILOT = FUNNEL_TRUST_PILOT) {
  trackCheckoutFunnelEvent("contact_details_started", { platform });
  sendMetaFormInteraction();
}

export function GA4orderSubmitted() {
  safeClarityEvent("Order Submitted");
  sendMetaOrderSubmitted();
  sendConsentGatedAnalyticsEvent("order_submitted", {
    platform: FUNNEL_TRUST_PILOT,
    frontend_domain: "reputech.io",
  });
}

export const handleSuccessfulOrder = async (
  dispatch: Dispatch,
  selectedCompanies: TrustPilotBusinessType[],
  priceItem: number,
  couponData: CouponType | null,
  _coupon: string,
  result: SuccessfulOrderResult,
  options: HandleSuccessfulOrderOptions,
) => {
  persistPaymentSetupToken(result);
  persistPlatformOrderResultSummary(
    options.funnelType,
    createPlatformOrderResultSummary(
      options.funnelType,
      selectedCompanies,
      priceItem,
      couponData,
      options.checkoutTotalPrice,
    ),
  );

  dispatch(resetReviewStateTrustPilot());
  await deletePersistentCart();
  await initializeUuidWithLock();
  clearPurchaseIdempotency();
};

export async function completeOrderAfterCardSaved(status: PaymentMethodStatus) {
  if (status.status !== "succeeded") return false;

  const token = readPaymentSetupToken();
  if (!token || !markPaymentSetupCompletionTracked(token)) return false;

  GA4orderSubmitted();
  const trackingUrl = readPaymentSetupTrackingUrl();
  if (trackingUrl && trackingService) {
    try {
      await trackingService.sendOrderTrackingData(trackingUrl, FUNNEL_TRUST_PILOT);
    } catch (error) {
      logSafeError("order-tracking-after-card-setup", error);
      reportToFlare(error, "order_tracking_after_card_setup");
    }
  }
  trackingService?.finishCheckout();
  return true;
}

export function GA4starRatingFilter(label: string, action: string) {
  GAClickEvent(label, action);
  safeClarityEvent(action);
  sendMetaReviewSelection(label, action);
}

export function GA4selectedReview(
  selectedReviewCount: number,
  platform: typeof FUNNEL_TRUST_PILOT = FUNNEL_TRUST_PILOT,
) {
  trackCheckoutFunnelEvent("review_selected", {
    platform,
    selectedReviewCount,
    dedupeKey: String(selectedReviewCount),
  });
  safeClarityEvent("review_selected");
  sendMetaReviewSelection("Selected Review", "review_selected");
}

export function GA4checkoutError(
  errorCategory: CheckoutErrorCategory,
  platform: typeof FUNNEL_TRUST_PILOT = FUNNEL_TRUST_PILOT,
) {
  trackCheckoutFunnelEvent("checkout_error", {
    platform,
    errorCategory,
    dedupeKey: errorCategory,
  });
}

export function AnalyticsContactFormEvent(source = "main") {
  GAEvent("Contact Form", `contact_form_${source}_page`);
  safeClarityEvent(`contact_form_${source}_page`);
  sendMetaContactFormEvent(source);
}
